import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import urls from '@src/@core/auth/jwt/urls'
import axios from 'axios'
import {errorToast} from "../../../components/messageToast/messageToast"

const axiosInstance = axios.create({
    baseURL: urls.url + '/conciliation/',
    headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
    }
})

export const getAllConciliations = createAsyncThunk(
    'conciliations/getAll',
    async (params, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get('/conciliations/', {
                params: {
                    p: params.page,
                    search: params.query,
                    page_size: params.page_size
                }
            })
            return response.data
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Error al cargar conciliaciones')
        }
    }
)

export const getConciliationMovements = createAsyncThunk(
    'conciliations/getConciliationMovements',
    async (conciliationId, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/conciliations/${conciliationId}/movements/`)
            return response.data
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Error al Movimientos')
        }
    }
)

export const deleteConciliation = createAsyncThunk(
    'conciliations/delete',
    async (conciliationId, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.delete(`/conciliations/${conciliationId}/`)
            return {
                ...response.data,
                deletedId: conciliationId
            }
        } catch (error) {
            console.error('Error en deleteConciliation:', error)
            return rejectWithValue(error.response?.data || 'Error al eliminar conciliación')
        }
    }
)

export const getMovementDetail = createAsyncThunk(
    'conciliations/getMovementDetail',
    async ({ reconciliationId, movementId }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/conciliations/${reconciliationId}/movement_detail/?movement_id=${movementId}`)
            return response.data
        } catch (error) {
            console.error('Error en getDetail:', error)
            return rejectWithValue(error.response?.data || 'Error al obtener el detalle')
        }
    }
)

export const previewConciliation = createAsyncThunk(
    'conciliations/create',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post('/conciliations/preview_data/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
            return response.data
        } catch (error) {
            const backendError = error.response?.data?.error || 'Error desconocido'
            errorToast(`Error al agregar movimientos: ${backendError}`)
            return rejectWithValue(backendError)
        }
    }
)

export const saveConciliation = createAsyncThunk(
    'cashManagement/saveDebitAutomatic',
    async (newData, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post('/conciliations/save_movements/', newData)
            // sucessToast('Débito automático guardado correctamente')
            return response.data
        } catch (error) {
            // errorToast('Error al guardar los movimientos')
            return rejectWithValue(error.response.data)
        }
    }
)

/**
 * Nuevo endpoint v2 para guardar movimientos (solo "Guardar preview").
 *
 * Contrato de respuesta (siempre consistente, éxito o fallo):
 *   {
 *     is_success: bool,
 *     status: "completed" | "validation_failed" | "failed",
 *     message: string,
 *     data: {
 *       reconciliation_id: number|null,
 *       header: {...} | {},
 *       movements: [...],
 *       summary: { total_received, total_saved, total_failed, total_documents_linked }
 *     },
 *     errors: {
 *       header:    [ { field, code, message } ],
 *       movements: [ { row_index, keydata, concept, bank_receipt, transaction_date, errors: [...] } ],
 *       documents: [ { row_index, movement_keydata, movement_concept, document_id, no_factura, errors: [...] } ],
 *       global:    [ { code, message, detail } ]
 *     }
 *   }
 *
 * HTTP:
 *   201 -> completed
 *   422 -> validation_failed | failed (persistence)
 *   400 -> payload estructuralmente inválido
 *   500 -> error interno no controlado
 *
 * IMPORTANTE: tanto en éxito como en error se devuelve el MISMO sobre `response_payload`
 * ya parseado. El `fulfilled` recibe la carga completa; el `rejected` (rejectWithValue)
 * también, para que el componente pueda leer `status`, `message`, `errors`, `data`.
 */
export const saveMovementsV2 = createAsyncThunk(
    'conciliations/saveMovementsV2',
    async (newData, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(
                '/conciliations/save_movements_v2/',
                { newData }
            )
            return response.data
        } catch (error) {
            const backendData = error.response?.data
            if (backendData && typeof backendData === 'object' && 'status' in backendData) {
                // Respuesta bien formada por el backend (422/400/500 con el sobre v2)
                return rejectWithValue(backendData)
            }
            // Error de red o respuesta inesperada: envolvemos en el mismo contrato
            return rejectWithValue({
                is_success: false,
                status: 'failed',
                message: error?.message || 'Error de red al guardar los movimientos.',
                data: {
                    reconciliation_id: null,
                    header: {},
                    movements: [],
                    summary: { total_received: 0, total_saved: 0, total_failed: 0, total_documents_linked: 0 }
                },
                errors: {
                    header: [],
                    movements: [],
                    documents: [],
                    global: [
                        {
                        code: 'NETWORK_ERROR',
                        message: error?.message || 'Error de red al guardar los movimientos.',
                        detail: null
                    }
                    ]
                }
            })
        }
    }
)

export const applyConciliationManual = createAsyncThunk(
    'conciliations/applyConciliationManual',
    async ({reconciliationId, movementId, documentIds, amounts}, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(
                `/conciliations/${reconciliationId}/manual_reconcile/`,
                {
                    movement_id: movementId,
                    document_ids: documentIds,
                    amounts
                }
            )
            return response.data
        } catch (error) {
            console.error('Error en applyConciliationManual:', error)
            return rejectWithValue(error.response?.data || 'Error al aplicar conciliación manual')
        }
    }
)

export const saveSuggestedDocument = createAsyncThunk(
    'conciliations/saveSuggestedDocument',
    async ({reconciliationId, movementId, documentId, suggestedAmount}, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(
                `/conciliations/${reconciliationId}/save_suggested_document/`,
                {
                    movement_id: movementId,
                    document_id: documentId,
                    suggested_amount: suggestedAmount
                }
            )
            return response.data
        } catch (error) {
            console.error('Error en saveSuggestedDocument:', error)
            return rejectWithValue(error.response?.data || 'Error al aplicar conciliación manual')
        }
    }
)

export const applyMasiveConciliations = createAsyncThunk(
    'conciliations/applyMasiveConciliations',
    async ({reconciliationId, movements}, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(
                `/conciliations/${reconciliationId}/bulk_reconcile/`,
                {
                    movements
                }
            )
            return response.data
        } catch (error) {
            console.error('Error en applyConciliationManual:', error)
            return rejectWithValue(error.response?.data || 'Error al aplicar conciliación manual')
        }
    }
)


export const updateMovements = createAsyncThunk(
    'conciliations/updateDetails',
    async ({ id, details }, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.put(`/conciliations/${id}/update_movements/`, details)
            // sucessToast('Registros actualizados correctamente')
            dispatch(getConciliationMovements(id))
            return response.data
        } catch (error) {
            console.error('Error en updateMovements:', error)
            // errorToast('Error al actualizar movimientos')
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

/**
 * Nuevo endpoint v2 para el botón "Guardar" sobre una conciliación YA
 * persistida. Sincroniza las ConciliationDocument asociadas a cada movimiento
 * (delete/create/update) sin tocar los campos del movimiento en sí.
 *
 * URL: PUT /conciliations/{id}/update_movements_v2/
 * Body: { details: [ { id, suggested_documents:[...], transaction_date?, ... } ] }
 *   (también acepta la lista pelada por compatibilidad)
 *
 * Respuestas:
 *   200 -> completed
 *   404 -> reconciliación no encontrada
 *   422 -> validation_failed | failed (nada se guardó)
 *   400 -> payload inválido
 *   500 -> error interno
 *
 * Tanto fulfilled como rejected devuelven el sobre v2 completo.
 */
export const updateMovementsV2 = createAsyncThunk(
    'conciliations/updateMovementsV2',
    async ({ id, details }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.put(
                `/conciliations/${id}/update_movements_v2/`,
                { details }
            )
            return response.data
        } catch (error) {
            const backendData = error.response?.data
            if (backendData && typeof backendData === 'object' && 'status' in backendData) {
                return rejectWithValue(backendData)
            }
            return rejectWithValue({
                is_success: false,
                status: 'failed',
                message: error?.message || 'Error de red al actualizar los movimientos.',
                data: {
                    reconciliation_id: id || null,
                    header: {},
                    movements: [],
                    summary: { total_received: 0, total_saved: 0, total_failed: 0, total_documents_linked: 0 }
                },
                errors: {
                    header: [],
                    movements: [],
                    documents: [],
                    global: [
                        {
                            code: 'NETWORK_ERROR',
                            message: error?.message || 'Error de red al actualizar los movimientos.',
                            detail: null
                        }
                    ]
                }
            })
        }
    }
)

/**
 * Nuevo endpoint v2 para el botón "Finalizar" sobre una conciliación YA
 * persistida. Sincroniza las sugerencias y aplica los pagos (Contifico o
 * local) con el sobre v2 completo incluyendo errors.reconciliation
 * (is_external / is_rollbackable).
 *
 * URL: POST /conciliations/{reconciliationId}/bulk_reconcile_v2/
 * Body: { movements: [ { id, suggested_documents:[...], ... } ] }
 *
 * Respuestas:
 *   200 -> completed (todos los movimientos conciliados sin error)
 *   207 -> completed_with_errors (parcial: is_success puede ser true o false)
 *   422 -> validation_failed / payload inválido / errores pre-persistencia
 *   404 -> reconciliación no encontrada
 *   500 -> error interno
 *
 * Tanto fulfilled como rejected devuelven el sobre v2 completo.
 */
export const bulkReconcileV2 = createAsyncThunk(
    'conciliations/bulkReconcileV2',
    async ({ reconciliationId, movements }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(
                `/conciliations/${reconciliationId}/bulk_reconcile_v2/`,
                { movements }
            )
            return response.data
        } catch (error) {
            const backendData = error.response?.data
            if (backendData && typeof backendData === 'object' && 'status' in backendData) {
                return rejectWithValue(backendData)
            }
            return rejectWithValue({
                is_success: false,
                status: 'failed',
                message: error?.message || 'Error de red al conciliar los movimientos.',
                data: {
                    reconciliation_id: reconciliationId || null,
                    header: {},
                    movements: [],
                    summary: {
                        total_received: 0,
                        total_saved: 0,
                        total_reconciled: 0,
                        total_partial: 0,
                        total_failed_reconciliation: 0,
                        total_documents_linked: 0,
                        total_documents_paid: 0,
                        total_documents_failed: 0,
                        contifico_enabled: false
                    }
                },
                errors: {
                    header: [],
                    movements: [],
                    documents: [],
                    reconciliation: [],
                    global: [
                        {
                            code: 'NETWORK_ERROR',
                            message: error?.message || 'Error de red al conciliar los movimientos.',
                            detail: null
                        }
                    ]
                }
            })
        }
    }
)

export const saveAndReconcileMovements = createAsyncThunk(
    'conciliations/saveAndReconcile',
    async ({ newData, autoReconcile = false }, { rejectWithValue }) => {
        try {
            const payload = {
                newData,
                auto_reconcile: autoReconcile
            }
            const response = await axiosInstance.post('/conciliations/save_and_reconcile_movements/', payload)
            return response.data
        } catch (error) {
            const backendData = error.response?.data || {error: 'Error desconocido'}
            const backendMessage =
                backendData?.message ||
                backendData?.error ||
                backendData?.detail ||
                'Error desconocido'
            errorToast(`Error al guardar/conciliar movimientos: ${backendMessage}`)
            return rejectWithValue(backendData)
        }
    }
)

/**
 * Nuevo endpoint v2 para guardar + conciliar movimientos (botón "Guardar y Finalizar").
 *
 * Diferencias clave vs saveMovementsV2:
 *   - URL: /conciliations/save_and_reconcile_movements_v2/
 *   - Body: { newData: {...}, auto_reconcile: bool }
 *   - Añade errors.reconciliation: [ { row_index, movement_id, movement_keydata,
 *       movement_concept, document_id, no_factura, is_external, is_rollbackable,
 *       errors: [...] } ]
 *   - Añade summary: total_reconciled, total_partial, total_failed_reconciliation,
 *       total_documents_paid, total_documents_failed, contifico_enabled, auto_reconcile
 *   - Nuevo status: "completed_with_errors" (HTTP 207 Multi-Status) cuando el
 *       guardado fue OK pero la fase de conciliación tuvo fallos parciales.
 *
 * HTTP:
 *   201 -> completed
 *   207 -> completed_with_errors (is_success puede ser true o false)
 *   422 -> validation_failed | failed (persistencia antes de conciliar)
 *   400 -> payload inválido
 *   500 -> error interno
 *
 * Tanto fulfilled como rejected reciben/devuelven el sobre completo, para que
 * el componente pueda decidir según status/is_success.
 */
export const saveAndReconcileMovementsV2 = createAsyncThunk(
    'conciliations/saveAndReconcileMovementsV2',
    async ({ newData, autoReconcile = false }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(
                '/conciliations/save_and_reconcile_movements_v2/',
                { newData, auto_reconcile: autoReconcile }
            )
            return response.data
        } catch (error) {
            const backendData = error.response?.data
            if (backendData && typeof backendData === 'object' && 'status' in backendData) {
                return rejectWithValue(backendData)
            }
            return rejectWithValue({
                is_success: false,
                status: 'failed',
                message: error?.message || 'Error de red al guardar/conciliar los movimientos.',
                data: {
                    reconciliation_id: null,
                    header: {},
                    movements: [],
                    summary: {
                        total_received: 0,
                        total_saved: 0,
                        total_reconciled: 0,
                        total_partial: 0,
                        total_failed_reconciliation: 0,
                        total_documents_linked: 0,
                        total_documents_paid: 0,
                        total_documents_failed: 0,
                        contifico_enabled: false,
                        auto_reconcile: Boolean(autoReconcile)
                    }
                },
                errors: {
                    header: [],
                    movements: [],
                    documents: [],
                    reconciliation: [],
                    global: [
                        {
                        code: 'NETWORK_ERROR',
                        message: error?.message || 'Error de red al guardar/conciliar los movimientos.',
                        detail: null
                    }
                    ]
                }
            })
        }
    }
)

/**
 * retryDocumentReconciliationV2
 *
 * Reintenta conciliar UN único documento previamente fallido dentro de un
 * movimiento ya persistido. Usa el endpoint:
 *   POST /conciliations/{reconciliationId}/retry_reconciliation_document_v2/
 *
 * Body: { movement_id, document_id }
 *
 * Respuestas:
 *   200 -> completed (éxito o idempotente si ya estaba sincronizado)
 *   422 -> validation_failed | failed (error Contifico o de validación)
 *   404 -> no encontrado / no pertenece a la partner
 *   500 -> error interno
 *
 * Tanto fulfilled como rejected reciben/devuelven el sobre v2 completo.
 */
export const retryDocumentReconciliationV2 = createAsyncThunk(
    'conciliations/retryDocumentReconciliationV2',
    async ({ reconciliationId, movementId, documentId }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(
                `/conciliations/${reconciliationId}/retry_reconciliation_document_v2/`,
                { movement_id: movementId, document_id: documentId }
            )
            return response.data
        } catch (error) {
            const backendData = error.response?.data
            if (backendData && typeof backendData === 'object' && 'status' in backendData) {
                return rejectWithValue(backendData)
            }
            return rejectWithValue({
                is_success: false,
                status: 'failed',
                message: error?.message || 'Error de red al reintentar el documento.',
                data: {
                    reconciliation_id: reconciliationId || null,
                    document: null
                },
                errors: {
                    header: [],
                    movements: [],
                    documents: [],
                    reconciliation: [],
                    global: [
                        {
                            code: 'NETWORK_ERROR',
                            message: error?.message || 'Error de red al reintentar el documento.',
                            detail: null
                        }
                    ]
                }
            })
        }
    }
)

const appConciliationSlice = createSlice({
    name: 'appConciliation',
    initialState: {
        list: [],              // Lista de conciliaciones
        count: 0,              // Total registros
        header: null,          // Header de conciliación (preview)
        details: [],           // Movimientos de conciliación
        movementDetail: null,  // Detalle individual de movimiento
        loading: false,
        error: null,
        movementDetailLoading: false,
        movementDetailError: null
    },
    reducers: {
        clearMovementDetail: (state) => {
            state.movementDetail = null
        },
        clearConciliation: (state) => {
            state.header = null
            state.details = []
            state.error = null
            state.loading = false
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAllConciliations.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(getAllConciliations.fulfilled, (state, action) => {
                state.loading = false
                state.list = action.payload.results// array
                state.count = action.payload.count   // total registros
            })
            .addCase(getAllConciliations.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            .addCase(previewConciliation.pending, state => {
                state.loading = true
                state.error = null
            })
            .addCase(previewConciliation.fulfilled, (state, action) => {
                state.loading = false
                state.header = action.payload.header
                state.details = action.payload.detail.movements
            })
            .addCase(previewConciliation.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload || 'Error al previsualizar conciliación'
                state.header = null
                state.details = []
            })
            .addCase(getConciliationMovements.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(getConciliationMovements.fulfilled, (state, action) => {
                state.loading = false
                state.header = action.payload.header
                state.details = action.payload.detail // array
            })
            .addCase(getConciliationMovements.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            .addCase(deleteConciliation.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(deleteConciliation.fulfilled, (state, action) => {
                state.loading = false
                state.list = state.list.filter(item => item.id !== action.payload.deletedId)// Remover la conciliación eliminada del estado
                state.count = Math.max(0, state.count - 1)
            })
            .addCase(deleteConciliation.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            .addCase(getMovementDetail.pending, (state) => {
                state.movementDetailLoading = true
                state.movementDetailError = null
            })
            .addCase(getMovementDetail.fulfilled, (state, action) => {
                state.movementDetailLoading = false
                state.movementDetail = action.payload
            })
            .addCase(getMovementDetail.rejected, (state, action) => {
                state.movementDetailLoading = false
                state.movementDetailError = action.payload?.error || 'Error desconocido'
            })
            .addCase(applyConciliationManual.pending, state => {
                state.loading = true
            })
            .addCase(applyConciliationManual.fulfilled, state => {
                state.loading = false
            })
            .addCase(applyConciliationManual.rejected, state => {
                state.loading = false
            })
            .addCase(applyMasiveConciliations.pending, state => {
                state.loading = true
            })
            .addCase(applyMasiveConciliations.fulfilled, state => {
                state.loading = false
            })
            .addCase(applyMasiveConciliations.rejected, state => {
                state.loading = false
            })
            .addCase(saveSuggestedDocument.pending, state => {
                state.loading = true
            })
            .addCase(saveSuggestedDocument.fulfilled, state => {
                state.loading = false
            })
            .addCase(saveSuggestedDocument.rejected, state => {
                state.loading = false
            })
            .addCase(saveAndReconcileMovements.pending, (state) => {
                state.saveAndReconcileLoading = true
                state.saveAndReconcileError = null
                state.reconciliationSummary = null
            })
            .addCase(saveAndReconcileMovements.fulfilled, (state, action) => {
                state.saveAndReconcileLoading = false
                state.lastSavedConciliation = action.payload

                // Guardar los detalles de los movimientos
                if (action.payload.detail?.movements) {
                    state.details = action.payload.detail.movements
                }

                // Guardar el resumen de conciliación si existe
                if (action.payload.reconciliation_summary) {
                    state.reconciliationSummary = action.payload.reconciliation_summary
                }

                // Actualizar la lista de conciliaciones si ya existe
                if (state.data && Array.isArray(state.data)) {
                    const existingIndex = state.data.findIndex(item => item.id === action.payload.id)
                    if (existingIndex === -1) {
                        // Agregar nueva conciliación al principio de la lista
                        state.data.unshift({
                            id: action.payload.id,
                            ...action.payload.header,
                            created_at: new Date().toISOString()
                        })
                        state.count = state.count + 1
                    } else {
                        // Actualizar conciliación existente
                        state.data[existingIndex] = {
                            ...state.data[existingIndex],
                            ...action.payload.header
                        }
                    }
                }
            })
            .addCase(saveAndReconcileMovements.rejected, (state, action) => {
                state.saveAndReconcileLoading = false
                state.saveAndReconcileError = action.payload
            })
            .addCase(saveMovementsV2.pending, (state) => {
                state.saveMovementsV2Loading = true
                state.saveMovementsV2Error = null
                state.lastSaveMovementsV2 = null
            })
            .addCase(saveMovementsV2.fulfilled, (state, action) => {
                state.saveMovementsV2Loading = false
                state.lastSaveMovementsV2 = action.payload
                // Propagamos los movimientos guardados al store para mantener coherencia.
                const movementsSaved = action.payload?.data?.movements
                if (Array.isArray(movementsSaved) && movementsSaved.length > 0) {
                    state.details = movementsSaved
                }
                const header = action.payload?.data?.header
                if (header && typeof header === 'object' && Object.keys(header).length > 0) {
                    state.header = header
                }
            })
            .addCase(saveMovementsV2.rejected, (state, action) => {
                state.saveMovementsV2Loading = false
                state.saveMovementsV2Error = action.payload
                state.lastSaveMovementsV2 = action.payload
            })
            .addCase(saveAndReconcileMovementsV2.pending, (state) => {
                state.saveAndReconcileV2Loading = true
                state.saveAndReconcileV2Error = null
                state.lastSaveAndReconcileV2 = null
            })
            .addCase(saveAndReconcileMovementsV2.fulfilled, (state, action) => {
                state.saveAndReconcileV2Loading = false
                state.lastSaveAndReconcileV2 = action.payload
                const movementsSaved = action.payload?.data?.movements
                if (Array.isArray(movementsSaved) && movementsSaved.length > 0) {
                    state.details = movementsSaved
                }
                const header = action.payload?.data?.header
                if (header && typeof header === 'object' && Object.keys(header).length > 0) {
                    state.header = header
                }
            })
            .addCase(saveAndReconcileMovementsV2.rejected, (state, action) => {
                state.saveAndReconcileV2Loading = false
                state.saveAndReconcileV2Error = action.payload
                state.lastSaveAndReconcileV2 = action.payload
            })
            .addCase(retryDocumentReconciliationV2.pending, (state) => {
                state.retryDocumentV2Loading = true
                state.retryDocumentV2Error = null
                state.lastRetryDocumentV2 = null
            })
            .addCase(retryDocumentReconciliationV2.fulfilled, (state, action) => {
                state.retryDocumentV2Loading = false
                state.lastRetryDocumentV2 = action.payload
            })
            .addCase(retryDocumentReconciliationV2.rejected, (state, action) => {
                state.retryDocumentV2Loading = false
                state.retryDocumentV2Error = action.payload
                state.lastRetryDocumentV2 = action.payload
            })
            .addCase(updateMovementsV2.pending, (state) => {
                state.updateMovementsV2Loading = true
                state.updateMovementsV2Error = null
                state.lastUpdateMovementsV2 = null
            })
            .addCase(updateMovementsV2.fulfilled, (state, action) => {
                state.updateMovementsV2Loading = false
                state.lastUpdateMovementsV2 = action.payload
                const movementsSaved = action.payload?.data?.movements
                if (Array.isArray(movementsSaved) && movementsSaved.length > 0) {
                    state.details = movementsSaved
                }
                const header = action.payload?.data?.header
                if (header && typeof header === 'object' && Object.keys(header).length > 0) {
                    state.header = header
                }
            })
            .addCase(updateMovementsV2.rejected, (state, action) => {
                state.updateMovementsV2Loading = false
                state.updateMovementsV2Error = action.payload
                state.lastUpdateMovementsV2 = action.payload
            })
            .addCase(bulkReconcileV2.pending, (state) => {
                state.bulkReconcileV2Loading = true
                state.bulkReconcileV2Error = null
                state.lastBulkReconcileV2 = null
            })
            .addCase(bulkReconcileV2.fulfilled, (state, action) => {
                state.bulkReconcileV2Loading = false
                state.lastBulkReconcileV2 = action.payload
                const movementsSaved = action.payload?.data?.movements
                if (Array.isArray(movementsSaved) && movementsSaved.length > 0) {
                    state.details = movementsSaved
                }
                const header = action.payload?.data?.header
                if (header && typeof header === 'object' && Object.keys(header).length > 0) {
                    state.header = header
                }
            })
            .addCase(bulkReconcileV2.rejected, (state, action) => {
                state.bulkReconcileV2Loading = false
                state.bulkReconcileV2Error = action.payload
                state.lastBulkReconcileV2 = action.payload
            })
    }
})
export const {
    clearMovementDetail,
    clearReconciliationSummary,
    clearLastSavedConciliation,
    clearConciliation
} = appConciliationSlice.actions
export default appConciliationSlice.reducer
